import { Pagination, Spinner } from '@nextui-org/react'
import { Key, ReactElement, ReactNode, useEffect, useMemo, useState } from 'react'
import { TbArrowDown, TbArrowsSort, TbArrowUp } from 'react-icons/tb'

export interface LogiColumn<T = any> {
  name: string
  uid: string
  align?: 'start' | 'center' | 'end'
  sortable?: boolean
  sortBy?: (item: T) => string | number | null | undefined
}

interface Props<T> {
  columns: Array<LogiColumn<T>>
  items: T[]
  renderCell: (item: T, columnKey: string) => ReactNode
  rowKey: (item: T) => Key
  ariaLabel?: string
  loading?: boolean
  emptyText?: string
  rowsPerPage?: number
  bottomExtra?: ReactNode
}

type SortDir = 'ascending' | 'descending'

const PAGE_SIZES = [10, 25, 50, 100]

const ALIGN: Record<string, string> = {
  start: 'text-left',
  center: 'text-center',
  end: 'text-right'
}

function LogiTable<T> ({
  columns, items, renderCell, rowKey, ariaLabel = 'Tabla',
  loading = false, emptyText = 'Sin datos', rowsPerPage = 10, bottomExtra
}: Props<T>): ReactElement {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(rowsPerPage)
  const [sortCol, setSortCol] = useState('')
  const [sortDir, setSortDir] = useState<SortDir>('ascending')
  const paginar = rowsPerPage > 0

  const isSortable = (col: LogiColumn<T>): boolean =>
    col.sortable ?? (col.uid !== 'actions' && col.uid !== '')

  const handleSort = (uid: string) => {
    if (sortCol === uid) {
      setSortDir(d => d === 'ascending' ? 'descending' : 'ascending')
    } else {
      setSortCol(uid)
      setSortDir('ascending')
    }
  }

  const sortedItems = useMemo(() => {
    const col = columns.find(c => c.uid === sortCol)
    if (col === undefined || !isSortable(col)) return items
    const accessor = col.sortBy ?? ((it: T) => (it as any)[col.uid])
    const dir = sortDir === 'descending' ? -1 : 1
    return [...items].sort((a, b) => {
      const av = accessor(a)
      const bv = accessor(b)
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      const cmp = typeof av === 'number' && typeof bv === 'number'
        ? av - bv
        : String(av).localeCompare(String(bv), 'es', { numeric: true, sensitivity: 'base' })
      return cmp * dir
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, sortCol, sortDir, columns])

  const totalPages = paginar ? Math.max(1, Math.ceil(sortedItems.length / pageSize)) : 1
  const safePage = Math.min(page, totalPages)

  useEffect(() => { setPage(1) }, [items.length, sortCol, sortDir, pageSize])

  const paged = useMemo(
    () => paginar ? sortedItems.slice((safePage - 1) * pageSize, safePage * pageSize) : sortedItems,
    [sortedItems, safePage, paginar, pageSize]
  )

  const from = sortedItems.length === 0 ? 0 : (safePage - 1) * pageSize + 1
  const to = Math.min(safePage * pageSize, sortedItems.length)
  const showPageSize = paginar && sortedItems.length > PAGE_SIZES[0]

  return (
    <div>
      <div className='overflow-x-auto'>
        <table aria-label={ariaLabel} className='w-full text-[13px] border-collapse'>
          <thead>
            <tr className='bg-neutral-50'>
              {columns.map(col => {
                const sortable = isSortable(col)
                const isActive = sortCol === col.uid
                const align = col.align ?? (col.uid === 'actions' ? 'end' : 'start')
                return (
                  <th
                    key={col.uid}
                    className={`text-[11px] font-medium text-neutral-500 uppercase tracking-wider px-3 py-2 border-b border-neutral-100 ${ALIGN[align]} ${sortable ? 'cursor-pointer select-none hover:text-neutral-700' : ''}`}
                    onClick={sortable ? () => handleSort(col.uid) : undefined}
                  >
                    <span className='inline-flex items-center gap-1'>
                      {col.name}
                      {sortable && (
                        <span className='text-neutral-400'>
                          {isActive
                            ? sortDir === 'ascending'
                              ? <TbArrowUp size={11} className='text-neutral-600' />
                              : <TbArrowDown size={11} className='text-neutral-600' />
                            : <TbArrowsSort size={11} />}
                        </span>
                      )}
                    </span>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={columns.length} className='text-center py-8'>
                  <Spinner size='sm' label='Cargando…' />
                </td>
              </tr>
            )}
            {!loading && paged.length === 0 && (
              <tr>
                <td colSpan={columns.length} className='text-center text-[12px] text-neutral-400 py-6'>
                  {emptyText}
                </td>
              </tr>
            )}
            {!loading && (paged as T[]).map(item => (
              <tr key={rowKey(item)} className='hover:bg-neutral-50/60 transition-colors'>
                {columns.map(col => {
                  const align = col.align ?? (col.uid === 'actions' ? 'end' : 'start')
                  return (
                    <td key={col.uid} className={`px-3 py-2.5 text-[12px] text-neutral-700 border-b border-neutral-100 ${ALIGN[align]}`}>
                      {renderCell(item, col.uid)}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(paginar || bottomExtra) && (
        <div className='flex flex-col gap-2'>
          {paginar && sortedItems.length > 0 && (
            <div className='flex items-center justify-between px-3 py-3 border-t border-neutral-100'>
              <p className='text-[12px] text-neutral-500'>
                Mostrando <span className='font-medium text-neutral-700'>{from}–{to}</span> de{' '}
                <span className='font-medium text-neutral-700'>{sortedItems.length}</span>
              </p>
              <div className='flex items-center gap-4'>
                {showPageSize && (
                  <div className='flex items-center gap-2 text-xs text-neutral-500'>
                    <span>Filas por página</span>
                    <select
                      aria-label='Filas por página'
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1) }}
                      className='h-7 rounded-md border border-neutral-200 bg-white px-2 text-xs text-neutral-700 focus:outline-none focus:border-neutral-400'
                    >
                      {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                )}
                {totalPages > 1 && (
                  <Pagination
                    size='sm'
                    total={totalPages}
                    page={safePage}
                    onChange={setPage}
                    showControls
                    variant='light'
                  />
                )}
              </div>
            </div>
          )}
          {bottomExtra && <div className='px-3 pb-3'>{bottomExtra}</div>}
        </div>
      )}
    </div>
  )
}

export default LogiTable
